import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, Text, ScrollView, Pressable,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import ProfileIcon60 from '../60pxIcons/ProfileIcon60';
import CustomTextField from '../CustomTextField';
import Button from '../Button';
import isMobile from '../IsMobile';
import AlertModal from '../AlertModal';
import Style from '../../constants/Style';
import { personNameRegex } from '../Regex';
import { generateAvatarColor } from '../../screens/Tickets/data';
import { SOMETHING_WENT_WRONG } from '../../constants/AlertMessages';
import { updateUsername } from '../../redux/userSlice';
import editMyProfile from '../../requests/Users/EditMyProfile';

interface PersonalInformationProps {
  email: string,
  setLoading: Dispatch<SetStateAction<boolean>>,
}

const maxLength: number = 50;

const PersonalInformation = ({ email, setLoading }: PersonalInformationProps) => {
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string>(null);
  const [lastNameError, setLastNameError] = useState<string>(null);
  const currentFirstName = useSelector((state: { user: { firstName: string | null } }) => state.user
    .firstName);
  const currentLastName = useSelector((state: { user: { lastName: string | null } }) => state.user
    .lastName);
  const [firstName, setFirstName] = useState<string>(currentFirstName ?? '');
  const [lastName, setLastName] = useState<string>(currentLastName ?? '');
  const dispatch = useDispatch();

  useEffect(() => {
    setFirstName(currentFirstName ?? '');
    setLastName(currentLastName ?? '');
  }, [currentFirstName, currentLastName]);
  const showAvatarStuff: boolean = false;
  const saveButtonDisabled: boolean = !!(firstNameError || lastNameError);

  const save: () => void = () => {
    setLoading(true);
    editMyProfile(email, firstName?.trim(), lastName?.trim()).then((res) => {
      if (!res || res?.isError) {
        setAlertTitle(res?.message ?? SOMETHING_WENT_WRONG);
        setAlertError(true);
      } else {
        setAlertTitle(res?.message ?? 'User edited successfully!');
        setAlertError(false);
        dispatch(updateUsername({ firstName: firstName?.trim(), lastName: lastName?.trim() }));
      }
      setAlertVisible(true);
    }).catch((e) => {
      setAlertTitle(e?.message ?? SOMETHING_WENT_WRONG);
      setAlertError(true);
      setAlertVisible(true);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    const text: string | null = firstName?.trim();
    if (!(text?.length > 0)) setFirstNameError('First name cannot be empty');
    else if (!(personNameRegex.exp.test(text?.trim()))) {
      setFirstNameError(`First name${personNameRegex.message}`);
    } else setFirstNameError(null);
  }, [firstName]);

  useEffect(() => {
    const text: string | null = lastName?.trim();
    if (!(text?.length > 0)) setLastNameError('Last name cannot be empty');
    else if (!(personNameRegex.exp.test(text?.trim()))) {
      setLastNameError(`Last name${personNameRegex.message}`);
    } else setLastNameError(null);
  }, [lastName]);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
    >
      {!showAvatarStuff ? <View
        style={{
          width: 100,
          height: 100,
          borderRadius: 50,
          backgroundColor: generateAvatarColor(`${firstName} ${lastName}`.length),
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={styles.avatarText}>
          {`${(firstName || email)?.charAt(0)?.toUpperCase() ?? ''}${lastName?.charAt(0)?.toUpperCase() ?? ''}`}
        </Text>
      </View> : <></>}
      {showAvatarStuff ? <View style={styles.imageSection}>
        <ProfileIcon60 style={styles.userPic} />
        <View style={styles.buttonSection}>
          <Text style={styles.uploadAvatarTitle}>
            {'Upload avatar'}
          </Text>
          <View style={{ flexDirection: 'row' }}>
            <Pressable style={(state: { hovered: boolean, pressed: boolean }) => [
              styles.button, styles.uploadButton,
              state.hovered && Style.hovered, state.pressed && Style.pressed,
            ]}>
              <Text style={styles.buttonText}>
                {'Upload new'}
              </Text>
            </Pressable>
            <Pressable style={(state: { hovered: boolean, pressed: boolean }) => [
              styles.button, styles.deleteButton,
              state.hovered && Style.hovered, state.pressed && Style.pressed,
            ]}>
              <Text style={[styles.buttonText, styles.deleteButtonText]}>
                {'Delete'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View> : <></>}
      <View style={styles.fieldSection}>
        <View style={styles.nameSection}>
          <View
            style={styles.textField}
            testID='first-name-modal'
          >
            <CustomTextField
              title={'First name'}
              value={firstName}
              updateValue={setFirstName}
              errorText={firstNameError}
              invalid={!!firstNameError}
              {...{ maxLength }}
              testID='first-name-input-field'
            />
          </View>
          <View
            style={[styles.textField, styles.lastNameField]}
            testID='last-name-modal'
          >
            <CustomTextField
              title={'Last name'}
              value={lastName}
              updateValue={setLastName}
              errorText={lastNameError}
              invalid={!!lastNameError}
              {...{ maxLength }}
              testID='last-name-input-field'
            />
          </View>
        </View>
        <CustomTextField
          title={'Email'}
          value={email}
          editable={false}
          updateValue={() => { }}
        />
      </View>
      <View>
        <Button
          title='Save'
          affirmative
          style={styles.saveButton}
          onPress={save}
          disabled={saveButtonDisabled}
          testID={saveButtonDisabled ? 'personal-info-save-btn-disabled' : 'personal-info-save-btn'}
        />
      </View>
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        setModalVisible={setAlertVisible}
        action={() => { }}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: isMobile ? 24 : 0,
    marginBottom: 0,
    borderRadius: 24,
    backgroundColor: '#FFF',
  },
  content: {
    flexGrow: 1,
    padding: isMobile ? 20 : 24,
  },
  imageSection: {
    flexDirection: isMobile ? 'column' : 'row',
    marginBottom: 24,
  },
  userPic: {
    width: 120,
    height: 120,
    borderRadius: 60,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    alignSelf: isMobile ? 'center' : 'flex-start',
    marginBottom: isMobile ? 16 : undefined,
    marginEnd: isMobile ? undefined : 32,
  },
  buttonSection: {
    alignItems: isMobile ? 'center' : 'flex-start',
  },
  uploadAvatarTitle: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack400,
    marginBottom: isMobile ? 12 : 16,
  },
  button: {
    paddingHorizontal: 12,
    height: 32,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#323247',
    shadowOpacity: 0.06,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    width: isMobile ? 98 : undefined,
  },
  uploadButton: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  deleteButton: {
    marginStart: 8,
    borderColor: Colors.light.hereworksRed100,
    borderWidth: 1,
    backgroundColor: '#FFF',
  },
  buttonText: {
    color: '#FFF',
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
  },
  deleteButtonText: {
    color: Colors.light.hereworksRed100,
  },
  fieldSection: {
    maxWidth: 400,
    flex: 1,
  },
  saveButton: {
    marginTop: isMobile ? 24 : 40,
    flex: undefined,
    alignSelf: 'flex-start',
  },
  avatarText: {
    color: '#FFF',
    fontFamily: 'Roboto_400Regular',
    fontSize: 38,
  },
  nameSection: {
    flexDirection: 'row',
  },
  textField: {
    flex: 1,
  },
  lastNameField: {
    marginStart: 8,
  },
});

export default PersonalInformation;
